/**
 * CompanyDocuments — aba «Documentos» de Configurações (DOC13) e painel dos
 * obrigatórios do cadastro pendente (Escopo 2) — docs-cadastro, lote P3.
 *
 * Fonte: SPEC v5.4 (Escopo 1–3, 7, 9; R3, R4, R8–R10, R13, R16, R19, R23–R25,
 * R35; Modelo de dados «status»/«Pendência»; Contrato) e PLAN v1.6 (P3,
 * §Contrato, PE-2).
 *  - Conjunto PJ × PF escolhido pelo `doc` (CPF 11 ⇒ PF; CNPJ 14 ⇒ PJ), igual
 *    para Cedente e Parceiro/Originador (PF5). Indeterminável ⇒ nada a enviar
 *    (fail-closed).
 *  - `mode="pendente"`: só os obrigatórios do tipo, com o token de escopo
 *    (allowlist fechada); sem download (fora da allowlist do pendente).
 *  - Subir/substituir só com `canUpload` (admin|operator; DOC11); viewer só vê.
 *  - Baixar PDF também só admin|operator (owner C1009747: «viewer da empresa só
 *    vê lista e status») — o mesmo conjunto de papéis de `canUpload`.
 *  - NÃO existe «retirar» (DOC14): nenhum botão nem chamada de remoção.
 *  - Pendência e destaque de reprovado são DERIVADOS da lista (R8/R10/EM1).
 *  - KYC «Não enviado» (owner C1014075 Q3 + C1014087 U1–U3): `stale_uploads`
 *    do servidor (o BO decide; o front não recalcula) aparece DENTRO da
 *    categoria, depois dos vigentes, só com o selo; «Reenviar» = o mesmo envio
 *    do botão do cabeçalho; sem download; não conta na pendência.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatApiErrorDetail, formatDateTimeBR } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { AlertTriangle, Download, Loader2 } from "lucide-react";
import ContractUpload, {
  CATEGORY_LABELS, COPY_PERIODO_ROTULO, COPY_SESSAO_EXPIRADA, DOCUMENT_SETS, MAX_VIGENTES_OPCIONAL,
  PERSON_TYPE_FIELD, detailOrCopy, isScopeExpired, listCompanyDocuments, personTypeFromDoc, referencePeriodOptions,
  requestDownloadUrl, requiresReferencePeriod,
} from "./ContractUpload";
import { COPY_PENDENCIA, PendenciaDot, usePendenciaDocs } from "./pendenciaDocsContext";

// ---------------------------------------------------------------------------
// Copy aprovada pelo owner (C1009755: COPY-F4-PORTAL-PROPOSTA.md sha256
// 25e03b845c5b, 1.ª opção de cada linha), literal.
// ---------------------------------------------------------------------------
export const COPY_ABA_GERAL = "Geral";
export const COPY_OBRIGATORIO = "Obrigatório";
export const COPY_OPCIONAL = "Opcional";
export const COPY_ROTULO_MOTIVO = "Motivo:";
export { COPY_PENDENCIA };
export const COPY_BOTAO_BAIXAR = "Baixar";
export const COPY_ERRO_CARREGAR = "Não foi possível carregar os documentos. Tente novamente.";
export const COPY_TIPO_INDETERMINADO = "Não foi possível identificar os documentos exigidos para o seu cadastro. Entre em contato com o suporte.";
/** Owner C1014087 (literais): U1 selo da linha nunca confirmada; U2 botão de reenvio. */
export const COPY_SELO_NAO_ENVIADO = "Não enviado";
export const COPY_BOTAO_REENVIAR = "Reenviar";

/** Aba literal da SPEC (R18/DOC13: «Aba Documentos»). */
export const ABA_DOCUMENTOS = "Documentos";

/**
 * Status de UI — literais do Modelo de dados da SPEC (`status`): `uploading`
 * «enviando», `pending_review` «em análise», `approved` «aprovado»,
 * `rejected` «reprovado»; «bloqueado» = `rejected` com `scan_status=threat`
 * ou `reviewed_by='system-scan'`.
 */
const STATUS_LABELS = {
  uploading: "enviando",
  pending_review: "em análise",
  approved: "aprovado",
  rejected: "reprovado",
  bloqueado: "bloqueado",
};

const STATUS_STYLES = {
  uploading: "bg-zinc-100 text-zinc-700 border-zinc-200",
  pending_review: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  bloqueado: "bg-red-100 text-red-800 border-red-300",
};

/** Selo «Não enviado» (C1014087 U1): estilo próprio, âmbar. */
const ESTILO_SELO_NAO_ENVIADO = "bg-amber-100 text-amber-900 border-amber-300";

export function uiStatus(doc) {
  if (doc.status === "rejected"
      && (doc.scan_status === "threat" || doc.reviewed_by === "system-scan")) {
    return "bloqueado";
  }
  return doc.status;
}

/** Estados que contam como «tem o obrigatório» (SPEC «Pendência»). */
const COBRE_OBRIGATORIO = new Set(["pending_review", "approved"]);

/** Download: só scan limpo e estado baixável (o servidor é a guarda). */
function podeBaixar(doc) {
  return doc.scan_status === "clean" && COBRE_OBRIGATORIO.has(doc.status);
}

/**
 * Tipo de pessoa: `person_type` do servidor PREVALECE (P3-Q7; o Portal deriva-o
 * do `doc` do cadastro no GET do pendente); senão o `doc` local. Senão `null`
 * (fail-closed). As categorias presentes NÃO decidem o tipo (revisão Astra
 * L3): `identidade_socio` também existe em documentos legados de sócios de PJ
 * (SPEC v5.4 :114), logo não identifica PF.
 */
export function resolvePersonType(doc, data) {
  const fromServer = data?.[PERSON_TYPE_FIELD];
  if (fromServer === "pf" || fromServer === "pj") return fromServer;
  return personTypeFromDoc(doc);
}

/** Itens obrigatórios sem vigente `pending_review|approved` (pendência derivada). */
export function pendingRequired(personType, documents) {
  const set = DOCUMENT_SETS[personType] || [];
  return set
    .filter((i) => i.required)
    .filter((i) => !(documents || []).some(
      (d) => d.category === i.category && d.is_current !== false && COBRE_OBRIGATORIO.has(d.status)))
    .map((i) => i.category);
}

/**
 * Aviso de documento obrigatório pendente (R10; texto = `COPY_PENDENCIA`
 * aprovado + a lista dos itens que faltam). Usado na aba Documentos e, owner
 * C1009756/C1009769, também na aba inicial de Configurações (`PendenciaDocumentos`).
 */
function PendenciaAviso({ faltando, testid = "company-documents-pendencia" }) {
  if (!faltando || faltando.length === 0) return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-[13px] p-3 flex items-start gap-2"
         data-testid={testid}>
      <AlertTriangle className="h-4 w-4 mt-0.5" />
      <div>
        <div>{COPY_PENDENCIA}</div>
        <div className="mt-1 font-medium">{faltando.map((c) => CATEGORY_LABELS[c]).join(" · ")}</div>
      </div>
    </div>
  );
}

/**
 * Owner C1009756 (só o aviso) + C1009769: o aviso de documento obrigatório
 * pendente da aba Documentos aparece também na aba inicial de Configurações
 * (`/configuracoes` Cedente, `/perfil` Parceiro), com o MESMO texto. Lê a mesma
 * lista (sessão normal); tipo de pessoa indeterminável ou erro de leitura ⇒ sem
 * aviso aqui (a aba Documentos mostra o erro).
 */
export function PendenciaDocumentos({ doc }) {
  const [faltando, setFaltando] = useState([]);
  useEffect(() => {
    let vivo = true;
    listCompanyDocuments(null)
      .then((data) => {
        const personType = resolvePersonType(doc, data);
        if (!vivo || !personType) return;
        const docs = (data?.documents || []).filter((d) => d.is_current !== false);
        setFaltando(pendingRequired(personType, docs));
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [doc]);
  return <PendenciaAviso faltando={faltando} testid="config-geral-pendencia" />;
}

/**
 * `refreshKey` (revisão Astra M3): quando o pai muda o valor (o «atualizar» e o
 * polling da tela de status), a lista é lida de novo — inclusive para sair do
 * cartão de erro quando o serviço volta. Sem botão nem copy novos.
 */
export default function CompanyDocuments({
  doc, canUpload = false, scopeToken = null, mode = "empresa", now, refreshKey = 0,
}) {
  const pendente = mode === "pendente";
  // Período de referência escolhido por item (dre/balancete; C1009767). `now`
  // injetável nos testes (relógio das opções do seletor).
  const [periodos, setPeriodos] = useState({});
  const opcoesPeriodo = referencePeriodOptions(now || new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [scopeExpired, setScopeExpired] = useState(false);

  // Revisão Astra4 M3: só a consulta mais recente escreve o estado. Cada `load`
  // (efeito, `refreshKey` ou `onUploaded`) toma um número; resposta ou erro de
  // número ultrapassado é descartado, e o unmount ultrapassa todos.
  const ultimoPedido = useRef(0);
  useEffect(() => () => { ultimoPedido.current += 1; }, []);

  const load = useCallback(async () => {
    const pedido = ++ultimoPedido.current;
    const atual = () => pedido === ultimoPedido.current;
    try {
      const d = await listCompanyDocuments(pendente ? scopeToken : null);
      if (!atual()) return;
      setData(d);
      setLoadError(false);
      setScopeExpired(false);
    } catch (e) {
      if (!atual()) return;
      if (pendente && isScopeExpired(e)) {
        setScopeExpired(true);
        return;
      }
      setLoadError(true);
      // Revisão Astra L1: sem `detail` ⇒ copy aprovada nº 29, nunca o genérico do api.js.
      toast.error(detailOrCopy(e?.response?.data?.detail, COPY_ERRO_CARREGAR));
    } finally {
      if (atual()) setLoading(false);
    }
  }, [pendente, scopeToken]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Bolinha de pendência (owner C1014323): depois de um envio, o layout relê a
  // pendência (sem provider ⇒ no-op, ex.: cadastro pendente).
  const { refresh: refreshPendencia } = usePendenciaDocs();
  const aposEnvio = useCallback(() => {
    load();
    if (!pendente) refreshPendencia();
  }, [load, pendente, refreshPendencia]);

  const onDownload = async (documentId) => {
    try {
      const url = await requestDownloadUrl(documentId, null);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="zemo-card p-6 flex justify-center" data-testid="company-documents-loading">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (scopeExpired) {
    return (
      <div className="zemo-card p-6 text-sm text-zinc-700" data-testid="company-documents-sessao-expirada">
        <a href="/login" className="text-[#0055FF] hover:underline">{COPY_SESSAO_EXPIRADA}</a>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="zemo-card p-6 text-sm text-red-700" data-testid="company-documents-error">
        {COPY_ERRO_CARREGAR}
      </div>
    );
  }

  const personType = resolvePersonType(doc, data);
  if (!personType) {
    return (
      <div className="zemo-card p-6 text-sm text-zinc-700" data-testid="company-documents-tipo-indeterminado">
        {COPY_TIPO_INDETERMINADO}
      </div>
    );
  }

  const documents = (data?.documents || []).filter((d) => d.is_current !== false);
  // Só `documents` decide a pendência (U3): a stale nunca conta como entregue.
  const stales = data?.stale_uploads || [];
  const itens = DOCUMENT_SETS[personType].filter((i) => !pendente || i.required);
  const faltando = pendingRequired(personType, documents);

  return (
    <div className="zemo-card p-6 space-y-5" data-testid="company-documents" data-person-type={personType}>
      <h3 className="text-base font-semibold tracking-tight">{ABA_DOCUMENTOS}</h3>

      <PendenciaAviso faltando={faltando} />

      <div className="space-y-4">
        {itens.map((item) => {
          const vigentes = documents.filter((d) => d.category === item.category);
          const staleDaCategoria = stales.filter((s) => s.category === item.category);
          const podeAcrescentar = canUpload
            && (item.unico || vigentes.length < MAX_VIGENTES_OPCIONAL);
          const comPeriodo = canUpload && requiresReferencePeriod(item.category);
          const periodo = periodos[item.category] || "";
          return (
            <div key={item.category} className="rounded-md border border-[var(--zemo-border)] p-4"
                 data-testid={`doc-item-${item.category}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[13px] font-semibold text-zinc-900">{CATEGORY_LABELS[item.category]}</div>
                  <div className="text-[11px] text-zinc-500">
                    {item.required ? COPY_OBRIGATORIO : COPY_OPCIONAL}
                  </div>
                </div>
                {podeAcrescentar && (
                  <ContractUpload
                    category={item.category}
                    scopeToken={pendente ? scopeToken : null}
                    referencePeriod={periodo}
                    onUploaded={aposEnvio}
                    replace={item.unico && vigentes.length > 0}
                  />
                )}
              </div>

              {comPeriodo && (
                <label className="mt-3 flex items-center gap-2 text-[12px] text-zinc-700">
                  <span>{COPY_PERIODO_ROTULO}</span>
                  <select
                    value={periodo}
                    onChange={(e) => setPeriodos((p) => ({ ...p, [item.category]: e.target.value }))}
                    className="rounded-md border border-[var(--zemo-border)] bg-white px-2 py-1 text-[12px]"
                    data-testid={`doc-periodo-select-${item.category}`}
                  >
                    <option value="" />
                    {opcoesPeriodo.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              )}

              {(vigentes.length > 0 || staleDaCategoria.length > 0) && (
                <ul className="mt-3 space-y-2">
                  {vigentes.map((d) => {
                    const st = uiStatus(d);
                    const reprovado = d.status === "rejected";
                    return (
                      <li key={d.id}
                          className={`rounded-md border p-3 text-[12px] ${reprovado ? "border-red-300 bg-red-50/60" : "border-[var(--zemo-border)]"}`}
                          data-testid={`doc-vigente-${d.id}`}
                          data-destaque={reprovado ? "reprovado" : undefined}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex rounded border px-1.5 py-0.5 text-[11px] font-medium ${STATUS_STYLES[st] || ""}`}
                                  data-testid={`doc-status-${d.id}`}>
                              {STATUS_LABELS[st] || d.status}
                            </span>
                            <span className="text-zinc-500 font-mono-num">{formatDateTimeBR(d.created_at)}</span>
                            {d.reference_period && (
                              <span className="text-zinc-700" title={COPY_PERIODO_ROTULO}
                                    data-testid={`doc-periodo-${d.id}`}>{d.reference_period}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {!pendente && canUpload && podeBaixar(d) && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => onDownload(d.id)}
                                      data-testid={`doc-download-${d.id}`}>
                                <Download className="h-3.5 w-3.5 mr-1" /> {COPY_BOTAO_BAIXAR}
                              </Button>
                            )}
                            {canUpload && !item.unico && (
                              <ContractUpload
                                category={item.category}
                                scopeToken={pendente ? scopeToken : null}
                                replacesDocumentId={d.id}
                                referencePeriod={periodo}
                                onUploaded={aposEnvio}
                                replace
                              />
                            )}
                          </div>
                        </div>
                        {reprovado && d.review_notes && (
                          <div className="mt-2 text-red-800" data-testid={`doc-motivo-${d.id}`}>
                            <span className="font-semibold">{COPY_ROTULO_MOTIVO}</span> {d.review_notes}
                          </div>
                        )}
                      </li>
                    );
                  })}
                  {/* Linha stale (C1014087 U1–U3): NÃO reusa o <li> dos vigentes —
                      sem status, sem motivo, sem download. «Reenviar» com as MESMAS
                      props do botão do cabeçalho (sem `replacesDocumentId`) e o
                      mesmo gate `podeAcrescentar` (senão o BO dá 409). */}
                  {staleDaCategoria.map((s) => (
                    <li key={`stale-${s.id}`}
                        className="rounded-md border border-[var(--zemo-border)] p-3 text-[12px]"
                        data-testid={`doc-stale-${s.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex rounded border px-1.5 py-0.5 text-[11px] font-medium ${ESTILO_SELO_NAO_ENVIADO}`}
                                data-testid={`doc-stale-selo-${s.id}`}>
                            {COPY_SELO_NAO_ENVIADO}
                          </span>
                          <span className="text-zinc-500 font-mono-num">{formatDateTimeBR(s.created_at)}</span>
                          {s.reference_period && (
                            <span className="text-zinc-700" title={COPY_PERIODO_ROTULO}
                                  data-testid={`doc-stale-periodo-${s.id}`}>{s.reference_period}</span>
                          )}
                        </div>
                        {podeAcrescentar && (
                          <ContractUpload
                            label={COPY_BOTAO_REENVIAR}
                            testid={`doc-reenviar-${s.id}`}
                            category={item.category}
                            scopeToken={pendente ? scopeToken : null}
                            referencePeriod={periodo}
                            onUploaded={aposEnvio}
                            replace={item.unico && vigentes.length > 0}
                          />
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Barra de abas de Configurações (Cedente `/configuracoes`, Parceiro `/perfil`). */
export function ConfigTabs({ aba, onChange }) {
  const tabs = [
    ["geral", COPY_ABA_GERAL],
    ["documentos", ABA_DOCUMENTOS],
  ];
  return (
    <div className="flex gap-1 border-b border-[var(--zemo-border)]" role="tablist" data-testid="config-tabs">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={aba === key}
          onClick={() => onChange(key)}
          className={`px-3 py-2 text-[13px] font-medium -mb-px border-b-2 ${aba === key ? "border-[#0055FF] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}
          data-testid={`config-tab-${key}`}
        >
          {label}
          {key === "documentos" && (
            <PendenciaDot testid="config-tab-documentos-pendencia-dot" className="inline-block ml-1.5 align-middle" />
          )}
        </button>
      ))}
    </div>
  );
}
